import {useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
    getTimeslotsLatest,
    migrateAvailabilities,
    type TimeslotsCalculationStatus,
    triggerTimeslotsCalculation,
} from "../../api/tenantDetailApi";
import {formatDateTime} from "../../util/format";
import {colors, fonts} from "../../theme/tokens";
import {SectionCard} from "../ui/SectionCard";

type ActionStatus = "idle" | "loading" | "success" | "error";

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const FIELD_LABEL_KEYS: Record<string, string> = {
    status: "DETAIL_TIMESLOTS_FIELD_STATUS",
    lastRun: "DETAIL_TIMESLOTS_FIELD_LAST_RUN",
    startedAt: "DETAIL_TIMESLOTS_FIELD_STARTED_AT",
    completedAt: "DETAIL_TIMESLOTS_FIELD_COMPLETED_AT",
    employeeId: "DETAIL_TIMESLOTS_FIELD_EMPLOYEE_ID",
    triggerSource: "DETAIL_TIMESLOTS_FIELD_TRIGGER_SOURCE",
    errorMessage: "DETAIL_TIMESLOTS_FIELD_ERROR_MESSAGE",
    pendingCount: "DETAIL_TIMESLOTS_FIELD_PENDING",
    inProgressCount: "DETAIL_TIMESLOTS_FIELD_IN_PROGRESS",
};

function displayValue(value: unknown): string {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string") return ISO_INSTANT.test(value) ? formatDateTime(value) : value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return JSON.stringify(value);
}

export function MaintenanceSection({tenantId, number, id}: {tenantId: string; number: number; id: string}) {
    const {t} = useTranslation();
    const [migrateStatus, setMigrateStatus] = useState<ActionStatus>("idle");
    const [calculateStatus, setCalculateStatus] = useState<ActionStatus>("idle");
    const [latest, setLatest] = useState<TimeslotsCalculationStatus | null>(null);
    const [latestError, setLatestError] = useState<string | null>(null);
    const [latestLoading, setLatestLoading] = useState(false);

    const runMigration = async () => {
        setMigrateStatus("loading");
        try {
            await migrateAvailabilities(tenantId);
            setMigrateStatus("success");
        } catch {
            setMigrateStatus("error");
        }
    };

    const runCalculation = async () => {
        setCalculateStatus("loading");
        try {
            await triggerTimeslotsCalculation(tenantId);
            setCalculateStatus("success");
        } catch {
            setCalculateStatus("error");
        }
    };

    const loadLatest = async () => {
        setLatestLoading(true);
        setLatestError(null);
        try {
            setLatest(await getTimeslotsLatest(tenantId));
        } catch (e) {
            setLatestError(e instanceof Error ? e.message : t("DETAIL_FAILED"));
        } finally {
            setLatestLoading(false);
        }
    };

    const statusText = (status: ActionStatus, successKey: string) => {
        if (status === "success") return <Typography component="span" sx={{color: colors.success, fontSize: "12.5px"}} role="status">{t(successKey)}</Typography>;
        if (status === "error") return <Typography component="span" sx={{color: colors.error, fontSize: "12.5px"}} role="status">{t("DETAIL_FAILED")}</Typography>;
        return null;
    };

    const fields = latest ? Object.entries(latest) : [];

    return (
            <SectionCard id={id} number={number} title={t("DETAIL_MAINTENANCE")}>
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5}}>
                    <Button variant="contained" disabled={migrateStatus === "loading"} onClick={() => void runMigration()}>
                        {migrateStatus === "loading" ? t("DETAIL_MIGRATING") : t("DETAIL_MIGRATE_AVAILABILITIES")}
                    </Button>
                    {statusText(migrateStatus, "DETAIL_DONE")}
                    <Button
                            variant="contained"
                            color="success"
                            disabled={calculateStatus === "loading"}
                            onClick={() => void runCalculation()}
                            sx={{ml: 1}}
                    >
                        {calculateStatus === "loading" ? t("DETAIL_CALCULATING") : t("DETAIL_CALCULATE_TIMESLOTS")}
                    </Button>
                    {statusText(calculateStatus, "DETAIL_CALCULATION_STARTED")}
                    <Button variant="outlined" disabled={latestLoading} onClick={() => void loadLatest()}>
                        {t("DETAIL_VIEW_LATEST_TIMESLOTS")}
                    </Button>
                </Box>
                {latestError && <Alert severity="error" sx={{mt: 2}}>{latestError}</Alert>}
                {latest && (
                        <Box
                                aria-label={t("DETAIL_TIMESLOTS_LATEST_ARIA")}
                                role="region"
                                sx={{mt: 2, p: 1.5, borderRadius: "4px", border: `1px solid ${colors.cardBorder}`, backgroundColor: colors.tableHeaderAlt}}
                        >
                            {fields.length === 0 || (fields.length === 1 && latest.status == null) ? (
                                    <Typography variant="body2" sx={{color: colors.muted}}>{t("DETAIL_TIMESLOTS_NO_JOB")}</Typography>
                            ) : (
                                    <Box component="dl" sx={{display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2, rowGap: 0.5, m: 0, fontSize: "12.5px"}}>
                                        {fields.map(([key, value]) => (
                                                <Box key={key} sx={{display: "contents"}}>
                                                    <Box component="dt" sx={{color: colors.muted}}>{FIELD_LABEL_KEYS[key] ? t(FIELD_LABEL_KEYS[key]) : key}</Box>
                                                    <Box component="dd" sx={{m: 0, color: colors.ink, fontFamily: key.toLowerCase().endsWith("id") ? fonts.mono : undefined}}>
                                                        {displayValue(value)}
                                                    </Box>
                                                </Box>
                                        ))}
                                    </Box>
                            )}
                        </Box>
                )}
            </SectionCard>
    );
}
